import { ref, computed, watch, onUnmounted } from 'vue'
import { BLACK, WHITE } from './gameConstants.js'
import { createInitialBoard, formatSquare } from './boardUtils.js'
import {
  getValidMoves,
  getFlips,
  applyMoveWithDetails,
  calculateScore,
  getWinner
} from './gameRules.js'
import { getAIMove } from './aiLogic.js'
import { locale, t } from '@/i18n/index.js'
import {
  serializeGameRecord,
  parseGameRecord,
  defaultJsonFilename,
  buildGameRecordFilename,
  downloadTextFile
} from '@/utils/gameRecord.js'
import {
  readLocalSaves,
  appendLocalSave,
  removeLocalSave
} from '@/utils/gameLocalSaves.js'

const PLAYER_NAME_STORAGE_KEY = 'othello-player-names'

function clipPlayerName(v) {
  return String(v ?? '').trim().slice(0, 24)
}

function readStoredPlayerNames() {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(PLAYER_NAME_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if ('pvpBlack' in parsed || 'pvpWhite' in parsed || 'pvcBlack' in parsed) {
      return {
        pvpBlack: clipPlayerName(parsed.pvpBlack),
        pvpWhite: clipPlayerName(parsed.pvpWhite),
        pvcBlack: clipPlayerName(parsed.pvcBlack)
      }
    }
    // backward compatibility: old structure { black, white }
    return {
      pvpBlack: clipPlayerName(parsed.black),
      pvpWhite: clipPlayerName(parsed.white),
      pvcBlack: clipPlayerName(parsed.black)
    }
  } catch {
    return null
  }
}

function writeStoredPlayerNames({ pvpBlack, pvpWhite, pvcBlack }) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(
      PLAYER_NAME_STORAGE_KEY,
      JSON.stringify({
        pvpBlack: clipPlayerName(pvpBlack),
        pvpWhite: clipPlayerName(pvpWhite),
        pvcBlack: clipPlayerName(pvcBlack)
      })
    )
  } catch {
    // ignore
  }
}

/**
 * 根据手顺重建第 step 手之后的棋盘与棋子序号（step=0 为开局）
 * @param {Array<{ serial: number, player: number, index: number }>} log
 * @param {number} step 已执行手数 0 … log.length
 */
export function buildReplayState(log, step) {
  let b = createInitialBoard()
  const metas = Array.from({ length: 64 }, () => null)
  const n = Math.min(Math.max(0, step), log.length)
  for (let i = 0; i < n; i++) {
    const m = log[i]
    const details = applyMoveWithDetails(b, m.index, m.player)
    if (!details) break
    const serial = m.serial
    b = details.board
    metas[m.index] = { serial, flipped: false }
    for (const fi of details.flips) {
      metas[fi] = { serial, flipped: true }
    }
  }
  return { board: b, pieceMetas: metas }
}

/**
 * 游戏状态管理
 * @returns {Object} 游戏状态和方法
 */
export function useGameState() {
  // 响应式状态
  const board = ref(createInitialBoard())
  const currentPlayer = ref(BLACK)
  const gameMode = ref('pvc') // 'pvp' | 'pvc'
  const playerNameBlack = ref('')
  const playerNameWhite = ref('')
  const pvpNameBlack = ref('')
  const pvpNameWhite = ref('')
  const pvcNameBlack = ref('')
  const gameOver = ref(false)
  const winner = ref(null)
  const lastMoveIndex = ref(-1)
  /** 对方被跳过回合时的下一行棋方（无子可落的一方） */
  const skipPass = ref(null)
  const aiThinking = ref(false)
  const moveSerial = ref(0)
  const pieceMetas = ref(Array.from({ length: 64 }, () => null))
  const moveLog = ref([])
  /** 终局复盘：0=开局，k=执行完前 k 手后的局面，length=终局 */
  const replayCursor = ref(0)
  const turnStartedAt = ref(Date.now())
  const statusNowMs = ref(Date.now())

  const localSaves = ref(
    typeof window !== 'undefined' ? readLocalSaves() : []
  )

  function refreshLocalSaves() {
    if (typeof window === 'undefined') return
    localSaves.value = readLocalSaves()
  }

  function saveCurrentToLocalStorage() {
    const payload = JSON.parse(buildExportJson())
    appendLocalSave({
      label: defaultJsonFilename().replace(/\.json$/i, ''),
      moveCount: moveLog.value.length,
      payload
    })
    refreshLocalSaves()
  }

  function loadSavedFromLocal(id) {
    const entry = readLocalSaves().find((x) => x.id === id)
    if (!entry) {
      throw new Error('NOTFOUND')
    }
    importGameFromJson(JSON.stringify(entry.payload))
  }

  function removeSavedFromLocal(id) {
    removeLocalSave(id)
    refreshLocalSaves()
  }

  function applyDefaultPlayerNamesForCurrentMode() {
    void locale.value
    pvpNameBlack.value = t('player.default1')
    pvpNameWhite.value = t('player.default2')
    pvcNameBlack.value = t('player.default1')
  }

  function applyNamesByMode(mode) {
    if (mode === 'pvc') {
      playerNameBlack.value = pvcNameBlack.value
      playerNameWhite.value = t('roles.ai')
      return
    }
    playerNameBlack.value = pvpNameBlack.value
    playerNameWhite.value = pvpNameWhite.value
  }

  applyDefaultPlayerNamesForCurrentMode()
  applyNamesByMode(gameMode.value)
  const storedNames = readStoredPlayerNames()
  if (storedNames) {
    if (storedNames.pvpBlack) pvpNameBlack.value = storedNames.pvpBlack
    if (storedNames.pvpWhite) pvpNameWhite.value = storedNames.pvpWhite
    if (storedNames.pvcBlack) pvcNameBlack.value = storedNames.pvcBlack
    applyNamesByMode(gameMode.value)
  }

  watch([playerNameBlack, playerNameWhite, gameMode], ([black, white, mode]) => {
    if (mode === 'pvc') {
      pvcNameBlack.value = clipPlayerName(black) || t('player.default1')
      if (white !== t('roles.ai')) {
        playerNameWhite.value = t('roles.ai')
      }
    } else {
      pvpNameBlack.value = clipPlayerName(black) || t('player.default1')
      pvpNameWhite.value = clipPlayerName(white) || t('player.default2')
    }
    writeStoredPlayerNames({
      pvpBlack: pvpNameBlack.value,
      pvpWhite: pvpNameWhite.value,
      pvcBlack: pvcNameBlack.value
    })
  })

  watch(gameOver, (over) => {
    if (over) {
      replayCursor.value = moveLog.value.length
    }
  })

  const statusTimerId = typeof window !== 'undefined'
    ? window.setInterval(() => {
      statusNowMs.value = Date.now()
    }, 1000)
    : null

  onUnmounted(() => {
    if (statusTimerId != null) {
      clearInterval(statusTimerId)
    }
  })

  watch(currentPlayer, () => {
    turnStartedAt.value = Date.now()
    statusNowMs.value = turnStartedAt.value
  })

  /** 终局且存在手顺时的复盘快照；对局进行中为 null */
  const replayFrame = computed(() => {
    if (!gameOver.value) return null
    const log = moveLog.value
    if (log.length === 0) return null
    const step = Math.min(Math.max(0, replayCursor.value), log.length)
    return buildReplayState(log, step)
  })

  const displayBoard = computed(() => {
    if (!gameOver.value) return board.value
    const frame = replayFrame.value
    return frame ? frame.board : board.value
  })

  const displayPieceMetas = computed(() => {
    if (!gameOver.value) return pieceMetas.value
    const frame = replayFrame.value
    return frame ? frame.pieceMetas : pieceMetas.value
  })

  const displayLastMoveIndex = computed(() => {
    if (!gameOver.value) return lastMoveIndex.value
    const log = moveLog.value
    if (log.length === 0) return lastMoveIndex.value
    const step = Math.min(Math.max(0, replayCursor.value), log.length)
    if (step <= 0) return -1
    return log[step - 1].index
  })

  const scores = computed(() => calculateScore(displayBoard.value))

  const validMoves = computed(() => {
    if (gameOver.value) return []
    return getValidMoves(board.value, currentPlayer.value)
  })

  const validMoveSet = computed(() => new Set(validMoves.value))
  const moveAssistMap = computed(() => {
    const out = {}
    if (gameOver.value) return out
    for (const idx of validMoves.value) {
      const flips = getFlips(board.value, idx, currentPlayer.value).length
      // 净增己方棋子：新落子 1 枚 + 翻转 flips 枚
      out[idx] = 1 + flips
    }
    return out
  })

  const displayNameBlack = computed(() => {
    void locale.value
    const v = playerNameBlack.value.trim().slice(0, 24)
    return v || t('player.default1')
  })

  const displayNameWhite = computed(() => {
    void locale.value
    if (gameMode.value === 'pvc') return t('roles.ai')
    const v = playerNameWhite.value.trim().slice(0, 24)
    return v || t('player.default2')
  })

  const canInteract = computed(() => {
    if (gameOver.value || aiThinking.value) return false
    if (gameMode.value === 'pvc' && currentPlayer.value === WHITE) return false
    return true
  })

  const statusBanner = computed(() => {
    void locale.value
    const name =
      currentPlayer.value === BLACK
        ? displayNameBlack.value
        : displayNameWhite.value
    const sec = Math.max(
      0,
      Math.floor((statusNowMs.value - turnStartedAt.value) / 1000)
    )
    return t('status.toMove', { name, sec })
  })

  const statusBannerRich = computed(() => {
    void locale.value
    const name =
      currentPlayer.value === BLACK
        ? displayNameBlack.value
        : displayNameWhite.value
    const sec = Math.max(
      0,
      Math.floor((statusNowMs.value - turnStartedAt.value) / 1000)
    )
    const tpl = t('status.toMove', { name: '__NAME__', sec: '__SEC__' })
    const [beforeName = '', afterNameAll = ''] = tpl.split('__NAME__')
    const [between = '', afterSec = ''] = afterNameAll.split('__SEC__')
    return {
      beforeName,
      name,
      between,
      sec: String(sec),
      afterSec
    }
  })

  const skipMessage = computed(() => {
    void locale.value
    const sp = skipPass.value
    if (!sp) return ''
    const np = sp.nextPlayer
    const who =
      np === BLACK ? displayNameBlack.value : displayNameWhite.value
    return t('game.skipLine', { who })
  })

  const resultTitle = computed(() => {
    void locale.value
    if (!winner.value) return ''
    if (winner.value === 'draw') return t('game.resultDraw')
    const winName =
      winner.value === 'black'
        ? displayNameBlack.value
        : displayNameWhite.value
    return t('game.resultNamedWin', { name: winName })
  })

  // 游戏方法
  function initGame() {
    board.value = createInitialBoard()
    currentPlayer.value = BLACK
    gameOver.value = false
    winner.value = null
    lastMoveIndex.value = -1
    skipPass.value = null
    aiThinking.value = false
    moveSerial.value = 0
    pieceMetas.value = Array.from({ length: 64 }, () => null)
    moveLog.value = []
    replayCursor.value = 0
    turnStartedAt.value = Date.now()
    statusNowMs.value = turnStartedAt.value
  }

  function makeMove(index) {
    if (!validMoveSet.value.has(index) || gameOver.value) {
      return false
    }

    const details = applyMoveWithDetails(board.value, index, currentPlayer.value)
    if (!details) {
      return false
    }

    moveSerial.value += 1
    const n = moveSerial.value
    const mover = currentPlayer.value
    const { board: newBoard, flips } = details

    const nextMeta = [...pieceMetas.value]
    nextMeta[index] = { serial: n, flipped: false }
    for (const fi of flips) {
      nextMeta[fi] = { serial: n, flipped: true }
    }
    pieceMetas.value = nextMeta

    moveLog.value = [
      ...moveLog.value,
      {
        serial: n,
        player: mover,
        index,
        square: formatSquare(index),
        flipCount: flips.length
      }
    ]

    board.value = newBoard
    lastMoveIndex.value = index

    return true
  }

  function nextTurn() {
    const nextPlayer = currentPlayer.value === BLACK ? WHITE : BLACK
    const nextMoves = getValidMoves(board.value, nextPlayer)
    const currentMoves = getValidMoves(board.value, currentPlayer.value)

    // 对方有棋可下，正常换手
    if (nextMoves.length > 0) {
      skipPass.value = null
      currentPlayer.value = nextPlayer
    }
    // 对方无棋可下，当前方继续
    else if (currentMoves.length > 0) {
      skipPass.value = { nextPlayer }
      turnStartedAt.value = Date.now()
      statusNowMs.value = turnStartedAt.value
    }
    // 双方都无棋可下，游戏结束
    else {
      gameOver.value = true
      winner.value = getWinner(board.value)
    }
  }

  function handlePlayerMove(index) {
    if (!canInteract.value || !validMoveSet.value.has(index)) {
      return false
    }

    if (makeMove(index)) {
      nextTurn()
      return true
    }
    return false
  }

  function makeAIMove() {
    const move = getAIMove(board.value, 'medium')
    if (move !== -1 && makeMove(move)) {
      nextTurn()
    }
    aiThinking.value = false
  }

  function switchMode(mode) {
    gameMode.value = mode
    applyNamesByMode(mode)
    initGame()
  }

  function setReplayStep(step) {
    if (!gameOver.value) return
    const n = moveLog.value.length
    replayCursor.value = Math.min(Math.max(0, step), n)
  }

  function buildExportJson() {
    return serializeGameRecord({
      gameMode: gameMode.value,
      gameOver: gameOver.value,
      winner: winner.value,
      moveLog: moveLog.value,
      playerNameBlack: playerNameBlack.value,
      playerNameWhite: playerNameWhite.value
    })
  }

  function downloadGameRecord() {
    if (typeof document === 'undefined') return
    const filename = buildGameRecordFilename(
      displayNameBlack.value,
      displayNameWhite.value
    )
    downloadTextFile(filename || defaultJsonFilename(), buildExportJson())
  }

  function importGameFromJson(text) {
    const data = parseGameRecord(text)
    try {
      initGame()
      gameMode.value = data.gameMode
      applyNamesByMode(data.gameMode)
      if (data.gameMode === 'pvc') {
        if (data.playerNameBlack) {
          pvcNameBlack.value = data.playerNameBlack
        }
      } else {
        if (data.playerNameBlack) {
          pvpNameBlack.value = data.playerNameBlack
        }
        if (data.playerNameWhite) {
          pvpNameWhite.value = data.playerNameWhite
        }
      }
      applyNamesByMode(data.gameMode)
      const log = [...data.moveLog].sort((a, b) => a.serial - b.serial)
      for (const m of log) {
        currentPlayer.value = m.player
        if (!makeMove(m.index)) {
          throw new Error('REPLAY')
        }
        nextTurn()
      }
      if (data.savedGameOver && !gameOver.value) {
        gameOver.value = true
        winner.value = data.winner ?? getWinner(board.value)
      }
      if (gameOver.value && data.winner && ['black', 'white', 'draw'].includes(data.winner)) {
        winner.value = data.winner
      }
      replayCursor.value = moveLog.value.length
    } catch (e) {
      initGame()
      throw e
    }
  }

  return {
    // 状态
    board,
    currentPlayer,
    gameMode,
    playerNameBlack,
    playerNameWhite,
    displayNameBlack,
    displayNameWhite,
    gameOver,
    winner,
    lastMoveIndex,
    skipPass,
    skipMessage,
    aiThinking,
    moveSerial,
    pieceMetas,
    moveLog,
    replayCursor,
    localSaves,

    // 计算属性
    displayBoard,
    displayPieceMetas,
    displayLastMoveIndex,
    scores,
    validMoves,
    validMoveSet,
    moveAssistMap,
    canInteract,
    statusBanner,
    statusBannerRich,
    resultTitle,

    // 方法
    initGame,
    makeMove,
    nextTurn,
    handlePlayerMove,
    makeAIMove,
    switchMode,
    setReplayStep,
    buildExportJson,
    downloadGameRecord,
    importGameFromJson,
    saveCurrentToLocalStorage,
    loadSavedFromLocal,
    removeSavedFromLocal,
    refreshLocalSaves
  }
}